import { MouseEvent, useState } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import { PAGES } from 'shared/config/constants';
import { LessonContent } from 'shared/api/class-stats';
import {
  Assignment,
  AssignmentContent,
  Brand,
} from 'shared/api/assignment-service';
import {
  CardEventHandlers,
  getPage,
  XApiEventKeys,
} from 'shared/api/event-service';
import {
  getDomainProgramLink,
  getImagePath,
  getLessonLink,
  getNewWindowLinkAttrs,
  isDesmosAssignment,
  isSSCAssignment,
  parseLesson,
} from 'shared/lib/utils';
import { loggerService } from 'shared/lib/logger';
import { dateService } from 'shared/lib/date';
import { Breadcrumbs } from 'shared/ui/breadcrumbs';
import { OvalButton } from 'shared/ui/buttons';
import { ClassCodeBadge } from 'shared/ui/class-code-badge';
// eslint-disable-next-line boundaries/element-types
import { AssignmentActionsMenu } from 'features/assignment-panel';

import styles from './styles.module.scss';

export type StreamCardProps = {
  classLabel: string;
  content: AssignmentContent | LessonContent;
  caption: string;
  eventHandlers: CardEventHandlers;
  assignment?: Assignment;
  startDate?: number | null;
  dueDate?: number | null;
  viewWorkButtonUrl?: string | null;
  readyLinkToLesson?: string;
  readyThumbnailUrl?: string;
  readyLessonNumber?: string;
  isWalkthroughActive?: boolean;
};

export const StreamCard = ({
  assignment,
  classLabel,
  content,
  caption,
  eventHandlers,
  startDate,
  dueDate,
  viewWorkButtonUrl,
  readyLinkToLesson,
  readyThumbnailUrl,
  readyLessonNumber,
  isWalkthroughActive,
}: StreamCardProps) => {
  const { t } = useTranslation();

  const [expirationDate, setExpirationDate] = useState('');
  const [isExpirationDateVisible, setIsExpirationDateVisible] = useState(false);

  const [parsedLessonNumber, lessonName] = parseLesson(
    content.title ?? (content as AssignmentContent).lessonTitle,
    content.brand === Brand.REGULAR,
  );

  const {
    onGoToLessonClick,
    onViewWorkClick,
    onProductBreadcrumbClick,
    onUnitBreadcrumbClick,
    onLessonBreadcrumbClick,
    onAssignmentActionsClick,
  } = eventHandlers;

  const formattedStartDate = startDate
    ? dateService.getFormattedDate(startDate, 'MMM DD')
    : null;
  const formattedDueDate = dueDate
    ? dateService.getFormattedDate(dueDate, 'MM/DD/YYYY')
    : null;
  const formattedDueTime = dueDate
    ? dateService.getTimeToDisplay(dueDate)
    : null;

  const lessonLink = readyLinkToLesson ?? getLessonLink(content);
  const lessonNumber = readyLessonNumber ?? parsedLessonNumber;

  const domainProgramLink = getDomainProgramLink(assignment);

  const thumbnailUrl =
    readyThumbnailUrl ?? getImagePath(content.lessonThumbnailImagePath);

  const isTutorial =
    content.brand !== Brand.CUSTOM && content.domain === 'Tutorial';

  const isDomainWithDistortedThumbnail =
    (content.domain === 'CKLA' || content.domain === 'Caminos') &&
    !isDesmosAssignment(assignment);

  const showDistortedThumbnail =
    content.brand !== Brand.CUSTOM &&
    isDomainWithDistortedThumbnail &&
    !isWalkthroughActive;

  const isSingleSessionCodeAssignment = isSSCAssignment(assignment);
  const classCode = isSingleSessionCodeAssignment
    ? assignment.content.classCode
    : '';
  const hasExpirationDatePassed = expirationDate
    ? dateService.getDayJSObject().isAfter(expirationDate)
    : false;

  const isArchived = Boolean(assignment?.archived);
  const isSSCInactive = isArchived || hasExpirationDatePassed;
  const shouldShowArchivedLabel = isSingleSessionCodeAssignment
    ? isArchived || isSSCInactive
    : isArchived;

  const handleGoToLessonClick = (
    e: MouseEvent<HTMLElement>,
    locationUrl?: string,
  ) => {
    if (e.type === 'contextmenu' && assignment) {
      onGoToLessonClick({
        url: window.location.href,
        page: getPage(),
        section: getPage() === PAGES.STREAM ? undefined : PAGES.STREAM,
        isRightClick: true,
      });

      return;
    }

    e.preventDefault();

    const redirectParams = getNewWindowLinkAttrs(content.brand);
    const target = redirectParams.target ? redirectParams.target : '_self';
    const features = redirectParams.rel ? 'noreferrer,noopener' : undefined;

    if (assignment && content.brand === Brand.CUSTOM) {
      /*
       * Do not wait until xApi event is sent for custom lessons - clicking on the button
       * will open url in the new tab. Redirection performs before async operation
       * to avoid tab blocking in the Safari.
       * */
      window.open(locationUrl, target, features);

      onGoToLessonClick({
        url: window.location.href,
        page: getPage(),
        section: getPage() === PAGES.STREAM ? undefined : 'stream',
        isRightClick: false,
      });

      return;
    }

    if (assignment && content.brand !== Brand.CUSTOM) {
      onGoToLessonClick({
        url: window.location.href,
        page: getPage(),
        section: getPage() === PAGES.STREAM ? undefined : 'stream',
        isRightClick: false,
      })
        .then(() => {
          window.open(locationUrl, target, features);
        })
        .catch((e) => {
          loggerService.logError(e);

          window.open(locationUrl, target, features);
        });

      return;
    }

    window.open(locationUrl, target, features);
  };

  const handleViewWorkClick = async (e: MouseEvent<HTMLElement>) => {
    await onViewWorkClick({
      url: window.location.href,
      page: getPage(),
      section: getPage() === PAGES.STREAM ? undefined : PAGES.STREAM,
      isRightClick: e.type === 'contextmenu',
    });
  };

  const handleDomainBadgeClick = async (
    e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
  ) => {
    await onProductBreadcrumbClick({
      isRightClick: e.type === 'contextmenu',
      url: window.location.href,
      page: getPage(),
      section: getPage() === PAGES.STREAM ? undefined : PAGES.STREAM,
    });
  };

  const handleBreadcrumbClick = async (
    e: MouseEvent<HTMLAnchorElement>,
    locationUrl?: string,
    isUnit?: boolean,
  ) => {
    if (e.type !== 'contextmenu') {
      e.preventDefault();
    }

    if (isUnit) {
      await onUnitBreadcrumbClick({
        isRightClick: e.type === 'contextmenu',
        url: window.location.href,
        page: getPage(),
        section: getPage() === PAGES.STREAM ? undefined : PAGES.STREAM,
      });
    } else {
      await onLessonBreadcrumbClick({
        isRightClick: e.type === 'contextmenu',
        url: window.location.href,
        page: getPage(),
        section: getPage() === PAGES.STREAM ? undefined : PAGES.STREAM,
      });
    }

    if (e.type !== 'contextmenu') {
      const redirectParams = getNewWindowLinkAttrs(content.brand);
      const target = redirectParams.target ? redirectParams.target : '_self';
      const features = redirectParams.rel ? 'noreferrer,noopener' : undefined;

      window.open(locationUrl, target, features);
    }
  };

  const handleAssignmentActionsXApiEvents = (
    eventType: XApiEventKeys,
    isRightClick = false,
  ) => {
    if (assignment) {
      onAssignmentActionsClick({
        eventType,
        isRightClick,
        assignment,
        url: window.location.href,
        page: getPage(),
        section: getPage() !== PAGES.STREAM ? 'stream' : undefined,
      });
    }
  };

  const handleShowExpirationDateClick = (
    fetchedExpirationTimestamp: string,
  ) => {
    const fetchedExpirationDate = dateService.getFormattedDate(
      fetchedExpirationTimestamp,
      'MM/DD/YYYY',
    );

    if (!expirationDate) {
      setExpirationDate(fetchedExpirationDate);
    }
    setIsExpirationDateVisible((prevIsVisible) => !prevIsVisible);
  };

  return (
    <article className={styles.streamCard} data-testid="stream-card">
      <div className={styles.thumbnailWrapper}>
        {content.brand !== Brand.REGULAR && (
          <div
            className={clsx(
              'd-flex',
              'justify-content-start',
              'align-items-start',
              styles.thumbnailLabel,
            )}
          >
            <span className={styles.thumbnailLabelText}>{t('customized')}</span>
          </div>
        )}
        <div
          className={clsx(styles.thumbnail, {
            [styles.distorted]: showDistortedThumbnail,
          })}
          style={{
            backgroundImage: `url("${thumbnailUrl}")`,
          }}
          aria-hidden
        />
        {shouldShowArchivedLabel && (
          <div className={styles.archived}>{t('Archived')}</div>
        )}
      </div>
      <div className="w-100">
        <div className="d-flex justify-content-between">
          <Breadcrumbs
            lessonLink={lessonLink}
            lessonNumber={lessonNumber}
            domain={
              content.brand === Brand.CUSTOM
                ? 'Slides Custom Created From Scratch'
                : content.domain
            }
            subject={'subject' in content ? content.subject : undefined}
            domainProgramLink={domainProgramLink}
            unitTitle={'unitTitle' in content ? content.unitTitle : undefined}
            unitKey={'unitKey' in content ? content.unitKey : undefined}
            isCustomized={content.brand === Brand.CUSTOMIZED}
            isCustom={content.brand === Brand.CUSTOM}
            isTutorial={isTutorial}
            onDomainBadgeClick={handleDomainBadgeClick}
            onBreadcrumbClick={handleBreadcrumbClick}
            isWalkthroughActive={isWalkthroughActive}
          />
          <div className={styles.actionsMenu}>
            {(assignment?.type === 'Lesson' ||
              assignment?.type === 'desmos' ||
              assignment?.type === 'Cardstack') && (
              <AssignmentActionsMenu
                assignment={assignment}
                onAssignmentActionsXApiEvents={
                  handleAssignmentActionsXApiEvents
                }
                onShowExpirationDateClick={handleShowExpirationDateClick}
              />
            )}
          </div>
        </div>
        <div className="d-xl-flex justify-content-between pt-1">
          <div className="text-width pr-2 pr-xl-3 pt-xl-2">
            <p className="scale-plus-1 mt-0 mb-2 mb-xl-0">
              {`${caption} `}{' '}
              <span className="font-weight-bolder">{lessonName}</span>
              {!isSingleSessionCodeAssignment && (
                <span>{` to ${classLabel} `}</span>
              )}
              {formattedStartDate && (
                <span data-testid="stream-card-start-date">
                  on
                  <span className="font-weight-bolder">{` ${formattedStartDate}`}</span>
                </span>
              )}
            </p>
            {isSingleSessionCodeAssignment && (
              <p className="scale-plus-1 mt-0 mb-2 mb-xl-0 d-flex align-items-center flex-wrap">
                {classCode && (
                  <>
                    <span className="font-weight-bolder">{t('Code')}</span>
                    <ClassCodeBadge
                      code={classCode}
                      isInactive={isSSCInactive}
                    />
                  </>
                )}
                {isExpirationDateVisible && expirationDate && (
                  <span className="d-flex align-items-center flex-wrap flex-sm-nowrap">
                    Expires on{' '}
                    <span className="font-weight-bolder ml-1">
                      {expirationDate}
                    </span>
                  </span>
                )}
              </p>
            )}
            {dueDate && (
              <div
                className={clsx(
                  'scale-minus-1-tight d-inline-block',
                  styles.dueDate,
                )}
              >
                {isWalkthroughActive
                  ? `Due Date: ${formattedDueDate}`
                  : `Due Date: ${formattedDueDate}, ${formattedDueTime}`}
              </div>
            )}
          </div>
          <div className="d-flex flex-column flex-sm-row justify-content-center justify-content-sm-end">
            {!isTutorial && (
              <OvalButton
                href={lessonLink}
                classNames="btn-secondary scale-minus-1 text-decoration-none mb-2 mb-sm-0"
                onClick={(e) => handleGoToLessonClick(e, lessonLink)}
                onContextMenu={(e) => handleGoToLessonClick(e, lessonLink)}
              >
                {t('Go to Lesson')}
              </OvalButton>
            )}
            {viewWorkButtonUrl && (
              <OvalButton
                href={viewWorkButtonUrl}
                classNames="btn-blue scale-minus-1 ml-0 ml-sm-2 text-decoration-none"
                target="_blank"
                rel="noreferrer noopener"
                onClick={handleViewWorkClick}
                onContextMenu={handleViewWorkClick}
              >
                {t('View Work')}
              </OvalButton>
            )}
          </div>
        </div>
      </div>
    </article>
  );
};
